/**
 * Feature Elements Mapping
 * Map feature codes với các UI element names để phục vụ phân quyền
 * Dựa trên docs/package-permission-system.md
 * Đồng bộ với database web.features
 */

// Feature codes - khớp với database
export const FEATURE_CODES = {
  DASHBOARD: 'FEATURE_DASHBOARD',
  PORTFOLIO: 'FEATURE_PORTFOLIO',
  CANSLIM: 'FEATURE_CANSLIM',
  CANSLIM_PATTERNS: 'FEATURE_CANSLIM_PATTERNS',
  SIGNAL: 'FEATURE_SIGNAL',
  WATCHLIST: 'FEATURE_WATCHLIST',
  PERSONA: 'FEATURE_PERSONA',
  SEARCH: 'FEATURE_SEARCH',
  VIDEO: 'FEATURE_VIDEO',
  PLANS: 'FEATURE_PLANS',
};

// Feature types
export const FEATURE_TYPES = {
  UI: 'UI',
  API: 'API',
  BOTH: 'BOTH',
};

// UI Element names mapping theo feature code - khớp với database web.features.ui_elements
export const FEATURE_UI_ELEMENTS = {
  // Dashboard feature (public)
  [FEATURE_CODES.DASHBOARD]: [
    'FintechDashboard',
    'SubHeader',
    'StatsGrid',
    'StockTable',
    'ButtonStyles',
  ],

  // Portfolio feature
  [FEATURE_CODES.PORTFOLIO]: [
    'Portfolio',
    'PortfolioStatsGrid',
    'HoldingsTable',
    'TransactionsTable',
  ],

  // CANSLIM Analysis feature
  [FEATURE_CODES.CANSLIM]: [
    'CanslimAnalysis',
    'KPIStats',
    'Leaderboard',
    'PatternCards',
    'BreakoutsTable',
    'HighlightCards',
  ],

  // CANSLIM Patterns Demo feature
  [FEATURE_CODES.CANSLIM_PATTERNS]: [
    'CanslimPatterns',
    'PatternCard',
    'BreakoutTable',
    'BreakoutSidePanel',
    'SkeletonCard',
    'DemoControls',
  ],

  // Signals & Alerts feature
  [FEATURE_CODES.SIGNAL]: [
    'SignalsAlerts',
    'SignalCard',
    'SignalsList',
    'AlertsTable',
    'NotificationsList',
    'CreateAlertModal',
  ],

  // Watchlist & Notes feature
  [FEATURE_CODES.WATCHLIST]: [
    'WatchlistNotes',
    'WatchlistSidebar',
    'SymbolsTable',
    'NotesPanel',
    'CreateListModal',
  ],

  // Persona Lens feature
  [FEATURE_CODES.PERSONA]: [
    'PersonaLens',
    'PersonaCard',
    'OnboardingWizard',
    'LensSwitcher',
    'PresetBanner',
    'PersonaStockTable',
    'InfoCards',
    'PersonaSubHeader',
    'OverviewSection',
  ],

  // Search Stock feature
  [FEATURE_CODES.SEARCH]: [
    'SearchStock',
    'SearchInput',
    'FilterPanel',
    'FilterChips',
    'ResultsTable',
    'Pagination',
    'StockHeader',
    'CompositeScoreCard',
    'FinancialFactsTable',
    'SearchSubHeader',
    'QuickActions',
  ],

  // Video Studio feature
  [FEATURE_CODES.VIDEO]: [
    'VideoStudio',
    'StudioHeader',
    'Sidebar',
    'CanvasPreview',
    'PlaybackControls',
    'SceneInspector',
    'Timeline',
  ],

  // Compare Plans (public)
  [FEATURE_CODES.PLANS]: [
    'ComparePlans',
    'PlansHeader',
    'PlansTable',
    'FAQSection',
    'PlansFooter',
  ],
};

// Public UI elements (không cần phân quyền)
export const PUBLIC_UI_ELEMENTS = [
  // Auth elements
  'AuthLayout',
  'AuthHeader',
  'AuthDivider',
  'AuthLinks',
  'SocialLogin',
  'ErrorMessage',
  'LoginForm',
  'RegisterForm',
  // Dashboard elements (luôn hiển thị)
  ...FEATURE_UI_ELEMENTS[FEATURE_CODES.DASHBOARD],
  // Compare Plans elements
  ...FEATURE_UI_ELEMENTS[FEATURE_CODES.PLANS],
];

// Helper: Lấy feature code từ element name
export const getFeatureByElement = (elementName) => {
  for (const [featureCode, elements] of Object.entries(FEATURE_UI_ELEMENTS)) {
    if (elements.includes(elementName)) {
      return featureCode;
    }
  }
  return null;
};

// Helper: Kiểm tra element có thuộc feature nào không (trừ public elements)
export const isProtectedElement = (elementName) => {
  if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
    return false;
  }
  return getFeatureByElement(elementName) !== null;
};

// Helper: Lấy tất cả UI elements của một feature
export const getElementsByFeature = (featureCode) => {
  return FEATURE_UI_ELEMENTS[featureCode] || [];
};

// Helper: Kiểm tra user có quyền xem element không
export const canAccessElement = (elementName, userFeatures = []) => {
  // Public elements luôn accessible
  if (PUBLIC_UI_ELEMENTS.includes(elementName)) {
    return true;
  }

  // Tìm feature chứa element này
  const featureCode = getFeatureByElement(elementName);
  if (!featureCode) {
    return true; // Element không trong danh sách bảo vệ
  }

  // Kiểm tra user có feature này không
  return userFeatures.some((f) => f.code === featureCode);
};

